import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = [
    'dropZone',
    'fileInput',
    'progressArea',
    'progressBar',
    'progressText',
    'errorList',
  ];
  static values = { url: String };

  connect() {
    // dragenter/dragleave also fire for child elements; track depth so the
    // highlight doesn't flicker while moving across the drop zone.
    this.dragDepth = 0;
    this.uploading = false;
  }

  browse(event) {
    // The "browse" label already opens the picker natively; don't open twice.
    if (event.target.closest('.library-upload-browse')) return;
    if (this.uploading) return;
    this.fileInputTarget.click();
  }

  dragEnter(event) {
    event.preventDefault();
    this.dragDepth++;
    this.dropZoneTarget.classList.add('drag-over');
  }

  dragOver(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add('drag-over');
  }

  dragLeave(event) {
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dropZoneTarget.classList.remove('drag-over');
    }
  }

  drop(event) {
    event.preventDefault();
    this.dragDepth = 0;
    this.dropZoneTarget.classList.remove('drag-over');

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      this.uploadFiles(files);
    }
  }

  selectFiles(event) {
    const files = event.target.files;
    if (files.length > 0) {
      this.uploadFiles(files);
    }
    // Allow re-selecting the same file later.
    event.target.value = '';
  }

  uploadFiles(fileList) {
    if (this.uploading) return;

    const files = Array.from(fileList);
    this.uploading = true;
    this.succeeded = 0;
    this.failed = 0;
    this.clearErrors();

    this.progressAreaTarget.style.display = 'block';
    this.setProgress(
      0,
      `Uploading ${
        files.length === 1 ? files[0].name : `${files.length} files`
      }...`
    );

    // Upload sequentially so several large files don't compete for bandwidth
    // and the progress bar stays meaningful.
    this.uploadNext(files, 0);
  }

  uploadNext(files, index) {
    if (index >= files.length) {
      this.finish(files.length);
      return;
    }

    const file = files[index];
    const position = `${index + 1} of ${files.length}`;
    const next = () => this.uploadNext(files, index + 1);

    const assetType = this.detectAssetType(file);
    if (!assetType) {
      this.failed++;
      this.addError(
        `${file.name}: unsupported file type. Upload audio, video, document, or image files.`
      );
      next();
      return;
    }

    const THREE_GIGABYTES = 3221225472;
    if (file.size > THREE_GIGABYTES) {
      this.failed++;
      this.addError(`${file.name}: larger than the 3 GB limit.`);
      next();
      return;
    }

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', e => {
      if (e.lengthComputable) {
        const filePercent = e.loaded / e.total;
        const overall = Math.min(
          99,
          Math.round(((index + filePercent) * 100) / files.length)
        );
        this.setProgress(
          overall,
          files.length > 1
            ? `Uploading ${position}: ${file.name} (${Math.round(
                filePercent * 100
              )}%)`
            : `Uploading: ${file.name} (${Math.round(filePercent * 100)}%)`
        );
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 400) {
        this.succeeded++;
      } else {
        this.failed++;
        this.addError(`${file.name}: ${this.errorMessage(xhr)}`);
      }
      next();
    });

    xhr.addEventListener('error', () => {
      this.failed++;
      this.addError(`${file.name}: network error. Please try again.`);
      next();
    });

    xhr.open('POST', this.urlValue, true);

    const data = new FormData();
    data.append('file', file);
    data.append('asset_type', assetType);

    const csrfToken = document.querySelector('meta[name="csrf-token"]');
    if (csrfToken) {
      xhr.setRequestHeader('X-CSRF-Token', csrfToken.content);
    }

    xhr.send(data);
  }

  finish(total) {
    this.uploading = false;

    if (this.failed === 0) {
      this.setProgress(
        100,
        total === 1
          ? 'Uploaded. Refreshing...'
          : `Uploaded ${total} files. Refreshing...`
      );
      // The active tab is kept in the URL by the tabs controller, so a
      // reload lands back on the same tab.
      setTimeout(() => location.reload(), 400);
    } else if (this.succeeded > 0) {
      this.setProgress(
        100,
        `Uploaded ${this.succeeded} of ${total} files. ${this.failed} failed (see below). Reload the page to see the new files.`
      );
    } else {
      this.setProgress(0, `Upload failed (${this.failed} of ${total}).`);
    }
  }

  errorMessage(xhr) {
    if (xhr.status === 422) {
      try {
        const errors = JSON.parse(xhr.response);
        return Object.values(errors)
          .flat()
          .join(', ');
      } catch (e) {
        return 'could not be uploaded.';
      }
    }
    return 'could not be uploaded. Please try again.';
  }

  setProgress(percent, text) {
    this.progressBarTarget.style.width = `${percent}%`;
    this.progressTextTarget.textContent = text;
  }

  addError(message) {
    const item = document.createElement('li');
    item.textContent = message;
    this.errorListTarget.appendChild(item);
    this.errorListTarget.style.display = 'block';
  }

  clearErrors() {
    this.errorListTarget.innerHTML = '';
    this.errorListTarget.style.display = 'none';
  }

  detectAssetType(file) {
    const recordingTypes = [
      'audio/mpeg',
      'audio/mp3',
      'audio/wave',
      'audio/wav',
      'audio/x-wav',
      'audio/x-pn-wav',
      'audio/vnd.wav',
      'audio/ogg',
      'audio/flac',
      'audio/x-flac',
      'audio/x-caf',
      'audio/webm',
      'audio/m4a',
      'audio/x-m4a',
      'audio/mp4',
      'audio/aac',
      'video/mp4',
      'video/webm',
      'video/quicktime',
      'video/x-m4v',
    ];

    const documentTypes = [
      'application/vnd.apple.pages',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/msword',
      'application/pdf',
      'text/plain',
    ];

    const contentType = file.type;

    if (recordingTypes.includes(contentType)) {
      return 'recording';
    } else if (documentTypes.includes(contentType)) {
      return 'document';
    } else if (contentType && contentType.startsWith('image/')) {
      return 'image';
    }

    // Some browsers/OSes report no MIME type for less common files
    // (e.g. .flac, .pages); fall back to the extension.
    return this.detectByExtension(file.name);
  }

  detectByExtension(fileName) {
    const ext = (fileName.split('.').pop() || '').toLowerCase();

    const recordingExts = [
      'mp3',
      'wav',
      'ogg',
      'flac',
      'm4a',
      'caf',
      'mp4',
      'webm',
      'weba',
      'mov',
      'm4v',
      'aac',
    ];
    const documentExts = ['pdf', 'doc', 'docx', 'pages', 'txt'];
    const imageExts = [
      'jpg',
      'jpeg',
      'png',
      'gif',
      'webp',
      'bmp',
      'heic',
      'tif',
      'tiff',
    ];

    if (recordingExts.includes(ext)) return 'recording';
    if (documentExts.includes(ext)) return 'document';
    if (imageExts.includes(ext)) return 'image';
    return null;
  }
}
